"use client"

import { paymentFields } from "@/actions/payment/client/payment-fields"
import { getFieldClasses } from "@/app/(site)/[locale]/checkout/CheckoutContent/components/address-form-layout-config"
import StepHeading from "@/app/(site)/[locale]/checkout/CheckoutContent/components/StepDetails/StepHeading"
import SelectCountry from "@/app/(site)/[locale]/checkout/CheckoutContent/Steps/CustomerInformation/CustomerFormFieldGroup/CountryStateZipSelectors/SelectCountry"
import SelectCountryState from "@/app/(site)/[locale]/checkout/CheckoutContent/Steps/CustomerInformation/CustomerFormFieldGroup/CountryStateZipSelectors/SelectCountryState"
import { type MarketLayout } from "@/app/(site)/[locale]/checkout/types"
import Input from "@/app/(site)/_components/Atoms/Inputs/Input"
import InputEmail from "@/app/(site)/_components/Atoms/Inputs/InputEmail"
import { useFieldValidationActions } from "@/app/(site)/_components/Atoms/Inputs/errors/fieldValidationRegistry"
import {
    createCombinedLengthValidator,
    poBoxValidator
} from "@/app/(site)/_components/Atoms/Inputs/errors/fieldValidators"
import InputZipCode from "@/app/(site)/_components/Atoms/Inputs/InputZipCode"
import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { useGetSameShippingAddress } from "@/app/(site)/_utils/_hooks/useGetSameShippingAddress"
import { useCheckoutFormStore } from "@/app/(site)/contexts/Global"
import { addressVerificationKey } from "@/lib/loqate/addressVerificationKey"
import { ADDRESS_AUTOCOMPLETE_ENABLED } from "@/lib/loqate/constants"
import { USER_HAS_SELECTED_STATE_STORAGE_KEY } from "@/lib/siteconfig/constants"
import { sanitizeAddress } from "@/lib/utils/checkout/sanitizeAddress"
import { clsx } from "@/src/utils/clsx"
import { t } from "@/translations"
import type { CountryCode } from "libphonenumber-js/min"
import dynamic from "next/dynamic"
import {
    type ChangeEvent,
    type ComponentProps,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react"

const TelInput = dynamic(
    async () => (await import("@/app/(site)/_components/Atoms/Inputs/TelInput")).default,
    { ssr: false }
)

const LoqateAddressAutocomplete = dynamic(
    async () =>
        (await import("@/app/(site)/_components/Atoms/Inputs/LoqateAddressAutocomplete"))
            .default
)

export enum AddressFormEnum {
    BILLING_ADDRESS = "billing",
    SHIPPING_ADDRESS = "shipping"
}
export type AddressFormType =
    | AddressFormEnum.BILLING_ADDRESS
    | AddressFormEnum.SHIPPING_ADDRESS

// name={`${type}_${FIELD_NAME}`} is written like this because it runs a validation on this match in Steps

interface AddressFormProps {
    type: AddressFormType
    title: string
    disabled?: boolean
    required?: boolean
    inputClassName?: string
    langCode: string
    marketLayout?: MarketLayout
    inputWrapperClassName?: string
}

/** Centra takes the street and the apartment as a single line of this length. */
const COMBINED_ADDRESS_LENGTH_LIMIT = 35

/** Fields Loqate needs before an address is worth verifying instead of autocompleting. */
const isAddressComplete = (
    formData: {
        address1: string
        zipCode: string
        city: string
        country: string
        state: string
    },
    hasStates: boolean
) =>
    !!formData.address1.trim() &&
    !!formData.zipCode.trim() &&
    !!formData.city.trim() &&
    !!formData.country.trim() &&
    (!hasStates || !!formData.state.trim())

export default function AddressForm({
    type,
    title,
    disabled = false,
    required = true,
    inputClassName = "",
    langCode,
    marketLayout,
    inputWrapperClassName = ""
}: AddressFormProps) {
    const { selection, location, countriesWithStates, isLoadingSelection, mutate } =
        useSWRBasketSessionState()
    const setBillingAddress = useCheckoutFormStore(state => state.setBillingAddress)
    const setShippingAddress = useCheckoutFormStore(state => state.setShippingAddress)
    const setVerifiedShippingAddressKey = useCheckoutFormStore(
        state => state.setVerifiedShippingAddressKey
    )
    const sameShippingAddress = useGetSameShippingAddress()
    const fieldValidation = useFieldValidationActions()

    const addressType =
        type === AddressFormEnum.BILLING_ADDRESS
            ? AddressFormEnum.BILLING_ADDRESS
            : AddressFormEnum.SHIPPING_ADDRESS
    const addressCountry =
        (addressType === AddressFormEnum.BILLING_ADDRESS
            ? selection?.address?.country
            : selection?.shippingAddress?.country) ?? ""
    const locationCountry = location?.country?.toUpperCase() ?? ""
    const defaultCountry =
        type === AddressFormEnum.SHIPPING_ADDRESS
            ? locationCountry || addressCountry || ""
            : addressCountry || locationCountry || ""
    const [selectedCountryCode, setSelectedCountryCode] = useState(defaultCountry)
    const [isLoadingChangeCountry, setIsLoadingChangeCountry] = useState(false)
    const [isCalculatingTax, setIsCalculatingTax] = useState(false)
    const [localFormData, setLocalFormData] = useState({
        email: "",
        firstName: "",
        lastName: "",
        address1: "",
        apartmentHouseNumber: "",
        address2: "",
        zipCode: "",
        city: "",
        country: "",
        state: "",
        phoneNumber: ""
    })

    const userHasSelectedState = () => {
        if (typeof window === "undefined") {
            return false
        }
        return sessionStorage.getItem(USER_HAS_SELECTED_STATE_STORAGE_KEY) === "true"
    }

    useEffect(() => {
        const at =
            type === AddressFormEnum.BILLING_ADDRESS
                ? AddressFormEnum.BILLING_ADDRESS
                : AddressFormEnum.SHIPPING_ADDRESS
        const addressData =
            at === AddressFormEnum.BILLING_ADDRESS
                ? selection?.address
                : selection?.shippingAddress
        const { billingAddress, shippingAddress } = useCheckoutFormStore.getState()
        const persisted =
            type === AddressFormEnum.BILLING_ADDRESS ? billingAddress : shippingAddress

        // For billing address, include country when sameShippingAddress is false
        const includeCountryForBilling =
            type === AddressFormEnum.BILLING_ADDRESS && !sameShippingAddress

        let next = { ...localFormData }
        if (addressData) {
            // `address1` verbatim: the old regex split moved the house number to the
            // apartment field, and submit merged both back as "Vintervägen 41 41".
            next = {
                ...next,
                email: addressData.email || next.email || "",
                firstName: addressData.firstName || next.firstName || "",
                lastName: addressData.lastName || next.lastName || "",
                address1: addressData.address1 || next.address1 || "",
                address2: addressData.address2 || next.address2 || "",
                zipCode: addressData.zipCode || next.zipCode || "",
                city: addressData.city || next.city || "",
                country: defaultCountry || next.country || "",
                phoneNumber: addressData.phoneNumber || next.phoneNumber || "",
                state: addressData.state || next.state || ""
            }
        }

        // Sanitize persisted data - include country for billing when different from shipping
        const sanitizedPersisted = sanitizeAddress(persisted, includeCountryForBilling)
        next = {
            ...next,
            ...sanitizedPersisted,
            // For billing address when different from shipping, prioritize country from persisted
            // Otherwise use country from selection/default
            country:
                includeCountryForBilling && persisted?.country
                    ? persisted.country
                    : next.country || defaultCountry
        }

        setLocalFormData(next)
        setSelectedCountryCode(next.country || defaultCountry)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(selection), type, sameShippingAddress])

    const hasStates = useMemo(() => {
        const currentCountryCode =
            localFormData?.country || selectedCountryCode || defaultCountry

        if (!currentCountryCode || !countriesWithStates?.length) {
            return false
        }
        const selectedCountryData = countriesWithStates.find(
            (country: any) => country.country === currentCountryCode
        )
        return (selectedCountryData?.states?.length ?? 0) > 0
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        localFormData?.country,
        selectedCountryCode,
        defaultCountry,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(countriesWithStates)
    ])

    /**
     * An address that arrived complete — autofill, a returning session, the back button —
     * is verified on submit rather than autocompleted. Only the starting position: the
     * field lets typing override it, because completeness cannot tell "replacing my
     * address" from "arrived with one" once a cleared line leaves the old city behind.
     */
    const suggestionsEnabled =
        ADDRESS_AUTOCOMPLETE_ENABLED && !isAddressComplete(localFormData, hasStates)

    const handleInputChange = (field: string, value: string) => {
        // This is because Centra is req us to send state, but we don't want to show this value to the user until they have selected it
        if (field === "state" && !userHasSelectedState()) {
            return
        }

        const shouldClearState = field === "country"
        const newData = {
            ...localFormData,
            [field]: value,
            ...(shouldClearState ? { state: "" } : {})
        }

        setLocalFormData(prev => ({
            ...prev,
            [field]: value,
            ...(shouldClearState ? { state: "" } : {})
        }))

        if (field === "country") {
            setSelectedCountryCode(value)
            if (type === AddressFormEnum.SHIPPING_ADDRESS) {
                fieldValidation?.clearReportedErrors()
            }
            const clearedData = {
                ...localFormData,
                country: value,
                state: ""
            }
            if (type === AddressFormEnum.BILLING_ADDRESS) {
                setBillingAddress(clearedData)
            } else {
                setShippingAddress(clearedData)
            }
        } else {
            if (type === AddressFormEnum.BILLING_ADDRESS) {
                setBillingAddress(newData)
            } else {
                setShippingAddress(newData)
            }
        }
    }

    /**
     * One update, so the store receives every field together rather than four partial
     * writes. A sub-premise goes to the apartment field: nothing is bound to `address2`,
     * so a value written there is invisible to the customer and dropped at submit, which
     * reads the form and not the store.
     */
    const handleAddressSelect = (address: {
        address1: string
        address2: string
        city: string
        state: string
        zipCode: string
    }) => {
        const nextData = {
            ...localFormData,
            address1: address.address1,
            apartmentHouseNumber: address.address2 || localFormData.apartmentHouseNumber,
            city: address.city,
            zipCode: address.zipCode,
            ...(address.state && userHasSelectedState() ? { state: address.state } : {})
        }

        setLocalFormData(nextData)

        if (type === AddressFormEnum.BILLING_ADDRESS) {
            setBillingAddress(nextData)
        } else {
            setShippingAddress(nextData)
            // Picking from the dropdown is itself a verification, so submit can skip the
            // Verify call while these values remain untouched.
            setVerifiedShippingAddressKey(addressVerificationKey(nextData))
        }
    }

    const previousEmailAddress = useRef(localFormData.email)
    const handleInputEmailBlur: ComponentProps<typeof InputEmail>["onBlur"] = useCallback(
        async (_: unknown, isValid: boolean) => {
            if (!isValid) {
                return
            }

            if (previousEmailAddress.current === localFormData.email) {
                return
            }

            previousEmailAddress.current = localFormData.email

            const email = localFormData.email

            // If the user changes the email address, we need to update it in the Centra
            // selection, so that the related domain-specific vouchers can be used.
            const result = await paymentFields({
                address: { email }
            })

            if (result.success) {
                await mutate()
            }
        },
        [localFormData.email, mutate]
    )

    const streetFieldSharedProps = {
        name: `${type}_address1`,
        label: t(langCode, "checkout.information_for_billing_and_shipping.address1"),
        value: localFormData.address1,
        onChange: (e: ChangeEvent<HTMLInputElement>) => {
            handleInputChange("address1", e.target.value)
        },
        required,
        disabled,
        langCode,
        autoComplete: `${type} address-line1`,
        inputClassName,
        containerClassName: getFieldClasses(marketLayout || "na", "address", hasStates),
        customValidators: [
            // A missing street number is Verify's call on submit: it can
            // tell a named premise from an incomplete street, and a check
            // for a digit cannot.
            ...(type !== AddressFormEnum.BILLING_ADDRESS ? [poBoxValidator] : []),
            createCombinedLengthValidator(
                () => localFormData.apartmentHouseNumber,
                COMBINED_ADDRESS_LENGTH_LIMIT
            )
        ],
        allowedCharacters: "-",
        isLoading: isLoadingChangeCountry || isLoadingSelection
    }

    return (
        <>
            {type === AddressFormEnum.SHIPPING_ADDRESS && (
                <div className="">
                    <StepHeading
                        title={t(
                            langCode,
                            "checkout.information_for_billing_and_shipping.email"
                        )}
                        langCode={langCode}
                        toStep={1}
                        showEditButton={false}
                        className="pb-16"
                    />

                    <InputEmail
                        name={`${type}_email`}
                        label={
                            t(
                                langCode,
                                "checkout.information_for_billing_and_shipping.email"
                            ) || "Email"
                        }
                        value={localFormData.email}
                        onChange={e => handleInputChange("email", e.target.value)}
                        required={required}
                        disabled={disabled}
                        langCode={langCode}
                        isLoading={isLoadingChangeCountry || isLoadingSelection}
                        inputClassName={inputClassName}
                        autoComplete={`${type} email`}
                        onBlur={handleInputEmailBlur}
                    />
                </div>
            )}

            <div className={clsx("pt-32", inputWrapperClassName)}>
                <StepHeading
                    title={title}
                    langCode={langCode}
                    toStep={1}
                    showEditButton={false}
                    className="pb-16"
                />
                <div className="grid grid-cols-1 gap-16 sm:grid-cols-2 md:grid-cols-6">
                    {/* First Name */}
                    <Input
                        name={`${type}_firstName`}
                        label={t(
                            langCode,
                            "checkout.information_for_billing_and_shipping.first_name"
                        )}
                        value={localFormData.firstName}
                        onChange={e => handleInputChange("firstName", e.target.value)}
                        required={required}
                        disabled={disabled}
                        langCode={langCode}
                        isLoading={isLoadingChangeCountry || isLoadingSelection}
                        inputClassName={inputClassName}
                        autoComplete={`${type} given-name`}
                        onBlurValidation={true}
                        containerClassName={getFieldClasses(
                            marketLayout || "na",
                            "firstName",
                            hasStates
                        )}
                    />

                    {/* Last Name */}
                    <Input
                        name={`${type}_lastName`}
                        label={t(
                            langCode,
                            "checkout.information_for_billing_and_shipping.last_name"
                        )}
                        value={localFormData.lastName}
                        onChange={e => handleInputChange("lastName", e.target.value)}
                        required={required}
                        disabled={disabled}
                        langCode={langCode}
                        autoComplete={`${type} family-name`}
                        onBlurValidation={true}
                        isLoading={isLoadingChangeCountry || isLoadingSelection}
                        inputClassName={inputClassName}
                        containerClassName={getFieldClasses(
                            marketLayout || "na",
                            "lastName",
                            hasStates
                        )}
                    />

                    {/* Address */}
                    {ADDRESS_AUTOCOMPLETE_ENABLED ? (
                        <LoqateAddressAutocomplete
                            {...streetFieldSharedProps}
                            onAddressSelect={handleAddressSelect}
                            country={selectedCountryCode || "US"}
                            suggestionsEnabled={suggestionsEnabled}
                        />
                    ) : (
                        <Input {...streetFieldSharedProps} />
                    )}

                    {/* Apartment/House Number */}
                    <Input
                        name={`${type}_apartmentHouseNumber`}
                        label={t(
                            langCode,
                            "checkout.information_for_billing_and_shipping.apartment_house_number"
                        )}
                        value={localFormData.apartmentHouseNumber}
                        onChange={e =>
                            handleInputChange("apartmentHouseNumber", e.target.value)
                        }
                        required={false} // due to different countries not necessarily have apartment/house number
                        disabled={disabled}
                        langCode={langCode}
                        autoComplete={`${type} apartment-number house-number`}
                        isLoading={isLoadingChangeCountry || isLoadingSelection}
                        inputClassName={inputClassName}
                        allowedCharacters="-"
                        containerClassName={getFieldClasses(
                            marketLayout || "na",
                            "apartmentHouseNumber",
                            hasStates
                        )}
                    />
                    {marketLayout !== "na" && !hasStates && (
                        <InputZipCode
                            label={t(
                                langCode,
                                "checkout.information_for_billing_and_shipping.zip_code"
                            )}
                            required={required}
                            selectedCountryCode={selectedCountryCode || ""}
                            name={`${type}_zipCode`}
                            country={selectedCountryCode}
                            state={localFormData.state}
                            value={localFormData.zipCode}
                            onChange={e => handleInputChange("zipCode", e.target.value)}
                            disabled={disabled}
                            autoComplete={`${type} postal-code`}
                            inputClassName={inputClassName}
                            langCode={langCode}
                            onBlurValidation={true}
                            isLoading={isLoadingChangeCountry || isLoadingSelection}
                            allowedCharacters="-"
                            disablePfasValidation={
                                type === AddressFormEnum.BILLING_ADDRESS
                            }
                            containerClassName={getFieldClasses(
                                marketLayout || "na",
                                "zipCode",
                                hasStates
                            )}
                        />
                    )}
                    {/* City */}
                    <Input
                        name={`${type}_city`}
                        label={t(
                            langCode,
                            "checkout.information_for_billing_and_shipping.city"
                        )}
                        value={localFormData.city}
                        onChange={e => handleInputChange("city", e.target.value)}
                        required={required}
                        disabled={disabled}
                        langCode={langCode}
                        autoComplete={`${type} address-level2`}
                        onBlurValidation={true}
                        isLoading={isLoadingChangeCountry || isLoadingSelection}
                        inputClassName={inputClassName}
                        allowedCharacters="-"
                        containerClassName={getFieldClasses(
                            marketLayout || "na",
                            "city",
                            hasStates
                        )}
                    />
                    {/* ZIP Code - This is to keep the correct tabindex for the zip code field */}
                    {marketLayout !== "na" && hasStates && (
                        <InputZipCode
                            label={t(
                                langCode,
                                "checkout.information_for_billing_and_shipping.zip_code"
                            )}
                            required={required}
                            selectedCountryCode={selectedCountryCode || ""}
                            name={`${type}_zipCode`}
                            country={selectedCountryCode}
                            state={localFormData.state}
                            value={localFormData.zipCode}
                            onChange={e => handleInputChange("zipCode", e.target.value)}
                            disabled={disabled}
                            autoComplete={`${type} postal-code`}
                            inputClassName={inputClassName}
                            langCode={langCode}
                            onBlurValidation={true}
                            isLoading={isLoadingChangeCountry || isLoadingSelection}
                            allowedCharacters="-"
                            disablePfasValidation={
                                type === AddressFormEnum.BILLING_ADDRESS
                            }
                            containerClassName={getFieldClasses(
                                marketLayout || "na",
                                "zipCode",
                                hasStates
                            )}
                        />
                    )}
                    {/* State - Only show if country has states */}
                    {hasStates && (
                        <SelectCountryState
                            value={localFormData.state}
                            disabled={disabled}
                            langCode={langCode}
                            isLoading={isLoadingChangeCountry || isLoadingSelection}
                            label={t(
                                langCode,
                                "checkout.information_for_billing_and_shipping.state"
                            )}
                            name={`${type}_state`}
                            required={required}
                            selectedCountryCode={selectedCountryCode || ""}
                            zipCode={localFormData.zipCode}
                            countries={countriesWithStates || []}
                            onChange={e => handleInputChange("state", e.target.value)}
                            formData={localFormData}
                            onTaxCalculationStart={() => setIsCalculatingTax(true)}
                            onTaxCalculationComplete={() => setIsCalculatingTax(false)}
                            inputClassName={inputClassName}
                            onBlurValidation={!!(marketLayout === "na")}
                            autoComplete={`${type} administrative-area-level-1`}
                            disablePfasValidation={
                                type === AddressFormEnum.BILLING_ADDRESS
                            }
                            addressType={type}
                            containerClassName={getFieldClasses(
                                marketLayout || "na",
                                "state",
                                hasStates
                            )}
                        />
                    )}

                    {/* ZIP Code - This is to keep the correct tabindex for the zip code field */}
                    {marketLayout === "na" && (
                        <InputZipCode
                            label={t(
                                langCode,
                                "checkout.information_for_billing_and_shipping.zip_code"
                            )}
                            required={required}
                            selectedCountryCode={selectedCountryCode || ""}
                            name={`${type}_zipCode`}
                            country={selectedCountryCode}
                            state={localFormData.state}
                            value={localFormData.zipCode}
                            onChange={e => handleInputChange("zipCode", e.target.value)}
                            disabled={disabled}
                            autoComplete={`${type} postal-code`}
                            inputClassName={inputClassName}
                            langCode={langCode}
                            onBlurValidation={true}
                            isLoading={isLoadingChangeCountry || isLoadingSelection}
                            allowedCharacters="-"
                            disablePfasValidation={
                                type === AddressFormEnum.BILLING_ADDRESS
                            }
                            containerClassName={getFieldClasses(
                                marketLayout || "na",
                                "zipCode",
                                hasStates
                            )}
                        />
                    )}

                    {/* Phone */}
                    <TelInput
                        defaultCountry={
                            (localFormData.country || location?.country) as CountryCode
                        }
                        name={`${type}_phoneNumber`}
                        label={t(
                            langCode,
                            "checkout.information_for_billing_and_shipping.phone_number"
                        )}
                        value={localFormData.phoneNumber}
                        onChange={e => handleInputChange("phoneNumber", e.target.value)}
                        disabled={disabled}
                        inputClassName={inputClassName}
                        langCode={langCode}
                        required={required}
                        autoComplete={`${type} tel`}
                        isLoading={isLoadingChangeCountry || isLoadingSelection}
                        containerClassName={getFieldClasses(
                            marketLayout || "na",
                            "phone",
                            hasStates
                        )}
                    />

                    {/* Country */}
                    <SelectCountry
                        value={localFormData.country || selectedCountryCode}
                        disabled={disabled}
                        langCode={langCode}
                        autoComplete="off"
                        isLoading={isLoadingChangeCountry || isLoadingSelection}
                        setIsLoadingChangeCountry={setIsLoadingChangeCountry}
                        setSelectedCountryCode={setSelectedCountryCode}
                        onChange={e => handleInputChange("country", e.target.value)}
                        label={t(
                            langCode,
                            "checkout.information_for_billing_and_shipping.country"
                        )}
                        name={`${type}_country`}
                        type={type}
                        required={required}
                        containerClassName={getFieldClasses(
                            marketLayout || "na",
                            "country",
                            hasStates
                        )}
                        inputClassName={inputClassName}
                    />
                </div>
            </div>
        </>
    )
}
